import React, { useState } from 'react';
import {
  Loader2,
  Upload,
  ChevronDown,
  AlertCircle,
  Infinity,
  FileText,
  CheckSquare,
  Square
} from 'lucide-react';
import { extractTextFromFile } from '../../utils/fileHandlers';
import { generateCoverLetter, generateResume } from '../../utils/openai';
import { ProgressBar } from '../ProgressBar';
import { CollapsibleFilters } from '../filters/CollapsibleFilters';
import { Paywall } from '../paywall/Paywall';
import { useAuth } from '../../contexts/AuthContext';
import { useBalance } from '../../contexts/BalanceContext';
import { decrementBalance } from '../../utils/firebase/balance';
import { uploadFileToStorage } from '../../utils/firebase/storage';
import { saveGeneration } from '../../utils/firebase/generations';
import { EditableLetter } from '../letter/EditableLetter';
import { JobRecommendations } from '../jobs/JobRecommendations';
import type { UserDocument } from '../../../types/document';
import type { FilterState } from '../Filters';

interface GenerationOptions {
  coverLetter: boolean;
  resume: boolean;
  jobRecommendations: boolean;
}

export function Generator() {
  const { user, subscription } = useAuth();
  const { balance, refreshBalance } = useBalance();

  // State management
  const [jobDescription, setJobDescription] = useState('');
  const [resumeContent, setResumeContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    length: 'medium',
    styles: ['formal'],
    purposes: ['job']
  });

  // Application Kit State
  const [generationOptions, setGenerationOptions] = useState<GenerationOptions>({
    coverLetter: true,
    resume: false,
    jobRecommendations: false
  });
  const [generatedContent, setGeneratedContent] = useState<{
    coverLetter?: string;
    resume?: string;
  }>({});
  const [showResults, setShowResults] = useState(false);

  const hasActiveSubscription =
    subscription?.status === 'active' || subscription?.status === 'trialing';
  const hasNoGenerationsLeft =
    !hasActiveSubscription &&
    balance &&
    balance.freeGenerations === 0 &&
    balance.paidGenerations === 0;

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');
    setUploadProgress(0);

    try {
      if (file.size > 10 * 1024 * 1024) {
        throw new Error(
          'File is too large. Please upload a smaller file (max 10MB).'
        );
      }

      if (user) {
        await uploadFileToStorage(file);
      }

      setUploadProgress(25);
      const content = await extractTextFromFile(file);
      setUploadProgress(75);

      if (content.startsWith('Error:')) {
        throw new Error(content);
      }

      setResumeContent(content);
      setFileName(file.name);
      setUploadProgress(100);
      setTimeout(() => setUploadProgress(0), 1000);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : 'Error processing file'
      );
      setUploadProgress(0);
      setFileName('');
      setResumeContent('');
    }
  };

  const handleGenerate = async () => {
    if (!jobDescription || !resumeContent) {
      setError('Please provide both job description and resume');
      return;
    }

    if (!user) {
      window.location.href = '/start';
      return;
    }

    if (
      !hasActiveSubscription &&
      (!balance ||
        (balance.freeGenerations === 0 && balance.paidGenerations === 0))
    ) {
      setShowPaywall(true);
      return;
    }

    setError('');
    setIsGenerating(true);
    setGenerationProgress(25);

    try {
      const newContent: typeof generatedContent = {};

      // Generate Cover Letter
      if (generationOptions.coverLetter) {
        const letter = await generateCoverLetter({
          jobDescription,
          resumeContent,
          options: { filters }
        });

        if (!letter.data) {
          throw new Error('Failed to generate cover letter');
        }

        newContent.coverLetter = letter.data;
        await saveGeneration(letter.data, jobDescription, filters, 'letter');
      }

      // Generate Resume
      if (generationOptions.resume) {
        const resume = await generateResume({
          jobDescription,
          resumeContent,
          options: { filters }
        });

        if (!resume.data) {
          throw new Error('Failed to generate resume');
        }

        newContent.resume = resume.data;
        await saveGeneration(resume.data, jobDescription, filters, 'resume');
      }

      if (!hasActiveSubscription) {
        const decremented = await decrementBalance(user.uid);
        if (!decremented) {
          throw new Error('Failed to process generation. Please try again.');
        }
        await refreshBalance();
      }

      setGenerationProgress(100);
      setGeneratedContent(newContent);
      setShowResults(true);
    } catch (error: any) {
      console.error('Generation error:', error);
      setError(error.message || 'Error generating content. Please try again.');
    } finally {
      setIsGenerating(false);
      setTimeout(() => setGenerationProgress(0), 1000);
    }
  };

  const toggleOption = (option: keyof GenerationOptions) => {
    setGenerationOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  if (showResults) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <h2 className="text-2xl font-bold mb-8">Your Application Kit</h2>
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Cover Letter Section */}
          {generatedContent.coverLetter && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-semibold mb-4">Cover Letter</h3>
              <EditableLetter
                content={generatedContent.coverLetter}
                jobDescription={jobDescription}
              />
            </div>
          )}

          {/* Resume Section */}
          {generatedContent.resume && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-semibold mb-4">Optimized Resume</h3>
              <EditableLetter
                content={generatedContent.resume}
                jobDescription={jobDescription}
              />
            </div>
          )}
        </div>

        {/* Job Recommendations Section */}
        {generationOptions.jobRecommendations && (
          <div className="mt-8">
            <JobRecommendations jobDescription={jobDescription} />
          </div>
        )}

        <div className="mt-8 text-center">
          <button
            onClick={() => setShowResults(false)}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
          >
            Generate Another Kit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Application Kit Generator</h1>

      {/* Pro Plan Notice */}
      {hasActiveSubscription ? (
        <div className="bg-primary/5 rounded-lg px-4 py-3 mb-6">
          <div className="flex items-center space-x-2 text-primary">
            <Infinity className="w-4 h-4" />
            <span className="text-sm font-medium">
              Unlimited generations with Pro plan
            </span>
          </div>
        </div>
      ) : (
        <div className="mb-6 text-sm text-slate-light">
          {balance && `Free generations remaining: ${balance.freeGenerations}`}
        </div>
      )}

      {/* No Generations Warning */}
      {!hasActiveSubscription && hasNoGenerationsLeft && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-primary mb-1">
                Oops, free tries finished!
              </h4>
              <p className="text-sm text-slate-light">
                Get unlimited generations with{' '}
                <a href="/pricing" className="text-primary hover:underline">
                  premium
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Generation Options */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
        <h3 className="font-medium mb-4">What would you like to generate?</h3>
        <div className="space-y-3">
          <button
            onClick={() => toggleOption('coverLetter')}
            className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <FileText className="w-5 h-5 text-primary" />
              <span>Cover Letter</span>
            </div>
            {generationOptions.coverLetter ? (
              <CheckSquare className="w-5 h-5 text-primary" />
            ) : (
              <Square className="w-5 h-5 text-gray-400" />
            )}
          </button>

          <button
            onClick={() => toggleOption('resume')}
            className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <FileText className="w-5 h-5 text-primary" />
              <span>Optimized Resume</span>
            </div>
            {generationOptions.resume ? (
              <CheckSquare className="w-5 h-5 text-primary" />
            ) : (
              <Square className="w-5 h-5 text-gray-400" />
            )}
          </button>

          <button
            onClick={() => toggleOption('jobRecommendations')}
            className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <FileText className="w-5 h-5 text-primary" />
              <span>Job Recommendations</span>
            </div>
            {generationOptions.jobRecommendations ? (
              <CheckSquare className="w-5 h-5 text-primary" />
            ) : (
              <Square className="w-5 h-5 text-gray-400" />
            )}
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-slate mb-2">
            Job Description
          </label>
          <textarea
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            className="w-full h-48 p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Paste the job description here..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate mb-2">
            Your Resume
          </label>
          <div className="relative">
            <div className="border-2 border-dashed rounded-lg p-4">
              <input
                type="file"
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.txt"
                className="hidden"
                id="resume-upload"
              />
              <label
                htmlFor="resume-upload"
                className="cursor-pointer flex items-center justify-center space-x-3"
              >
                <Upload className="h-5 w-5 text-slate-light" />
                <span className="text-sm text-slate-light">
                  {fileName ||
                    'Click to upload your resume (PDF, DOC, DOCX, or TXT)'}
                </span>
              </label>
            </div>
            {uploadProgress > 0 && (
              <div className="mt-2">
                <ProgressBar progress={uploadProgress} isGenerating={false} />
              </div>
            )}
          </div>
        </div>

        <CollapsibleFilters filters={filters} onFilterChange={setFilters} />

        {error && <div className="text-red-600 text-sm">{error}</div>}

        <button
          onClick={handleGenerate}
          disabled={
            isGenerating ||
            !jobDescription ||
            !resumeContent ||
            !Object.values(generationOptions).some(Boolean)
          }
          className="w-full bg-primary text-white py-3 rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isGenerating ? (
            <>
              <Loader2 className="animate-spin mr-2" />
              Generating...
            </>
          ) : (
            'Generate Application Kit'
          )}
        </button>

        {generationProgress > 0 && (
          <ProgressBar
            progress={generationProgress}
            isGenerating={isGenerating}
          />
        )}

        <Paywall isOpen={showPaywall} onClose={() => setShowPaywall(false)} />
      </div>
    </div>
  );
}